<template>
  <div class="payout-page">
    <section class="payout-toolbar">
      <div>
        <h2>Repasses</h2>
        <p>Saldo disponível, valores em disponibilização e solicitações de saque.</p>
      </div>
      <v-btn icon :loading="loading" title="Atualizar" @click="load">
        <v-icon size="21">mdi-refresh</v-icon>
      </v-btn>
    </section>

    <v-alert v-if="setupMessage" text type="warning" class="setup-alert">
      <div class="setup-alert-content">
        <span>{{ setupMessage }}</span>
        <v-btn text color="primary" @click="$router.push('/configuracoes/pagamentos')">
          <v-icon left size="18">mdi-credit-card-cog-outline</v-icon>
          Configurar pagamentos
        </v-btn>
      </div>
    </v-alert>

    <section class="summary-grid">
      <div class="summary-panel available">
        <span>Disponível para repasse</span>
        <strong>{{ formatCurrency(summary.available_amount) }}</strong>
        <small>{{ availableCaption }}</small>
      </div>
      <div class="summary-panel">
        <span>Pendente de disponibilização</span>
        <strong>{{ formatCurrency(summary.pending_release_amount) }}</strong>
        <small>Valores pagos ainda em liberação ou processamento</small>
      </div>
      <div class="summary-panel">
        <span>Repasses solicitados</span>
        <strong>{{ formatCurrency(summary.requested_transfer_amount) }}</strong>
        <small>{{ formatCurrency(summary.pending_transfer_amount) }} em andamento</small>
      </div>
    </section>

    <section class="payout-grid">
      <div class="request-panel">
        <div class="panel-heading">
          <div>
            <h3>Solicitar saque/repasse</h3>
            <p>O valor é enviado para a conta de repasse configurada.</p>
          </div>
          <v-icon color="primary" size="28">mdi-bank-transfer-out</v-icon>
        </div>

        <v-text-field
          v-model="form.amount"
          label="Valor do repasse"
          outlined
          dense
          prefix="R$"
          inputmode="decimal"
          :error-messages="amountErrors"
          hide-details="auto"
        />

        <div class="destination-line">
          <v-icon size="18">mdi-map-marker-check-outline</v-icon>
          <span>{{ destinationLabel }}</span>
        </div>

        <v-btn
          depressed
          color="primary"
          class="request-button"
          :loading="saving"
          :disabled="requestDisabled"
          @click="requestPayout"
        >
          <v-icon left size="19">mdi-cash-fast</v-icon>
          Fazer saque/repasse
        </v-btn>
      </div>

      <div class="history-panel">
        <div class="panel-heading">
          <div>
            <h3>Histórico recente</h3>
            <p>Últimas solicitações de repasse enviadas.</p>
          </div>
        </div>

        <BoaruaDataTable
          :headers="headers"
          :items="transfers"
          :loading="loading"
          item-key="id"
          hide-default-footer
          class="payout-table"
        >
          <template v-slot:item.amount="{ item }">
            <strong>{{ formatCurrency(item.amount) }}</strong>
          </template>
          <template v-slot:item.status="{ item }">
            <v-chip small :color="statusColor(item.status)" :text-color="statusTextColor(item.status)">
              {{ statusLabel(item.status) }}
            </v-chip>
          </template>
          <template v-slot:item.created_at="{ item }">
            {{ formatDate(item.created_at || item.requested_at) }}
          </template>
          <template v-slot:no-data>
            <div class="empty-state">
              <v-icon color="primary" size="32">mdi-bank-transfer</v-icon>
              <strong>Nenhum repasse solicitado</strong>
              <span>Os saques feitos pelo local aparecerão aqui.</span>
            </div>
          </template>
        </BoaruaDataTable>
      </div>
    </section>
  </div>
</template>

<script>
import axios from "axios";
import BoaruaDataTable from "@/views/boarua/components/BoaruaDataTable.vue";

const emptySummary = () => ({
  account_ready: false,
  payout_ready: false,
  account_status: "pending",
  account_configured: false,
  has_gateway_wallet: false,
  has_gateway_api_key: false,
  payout_type: null,
  available_amount: 0,
  pending_release_amount: 0,
  requested_transfer_amount: 0,
  pending_transfer_amount: 0,
  gateway_error: null,
});

export default {
  name: "PayoutTransfers",
  components: { BoaruaDataTable },
  data() {
    return {
      loading: false,
      saving: false,
      summary: emptySummary(),
      paymentAccount: {},
      transfers: [],
      form: {
        amount: "",
      },
      headers: [
        { text: "Valor", value: "amount", width: 150 },
        { text: "Status", value: "status", width: 140 },
        { text: "Retorno", value: "gateway_status", width: 140 },
        { text: "Solicitado em", value: "created_at", width: 170 },
      ],
    };
  },
  computed: {
    availableAmount() {
      return Number(this.summary.available_amount || 0);
    },
    parsedAmount() {
      return this.parseMoney(this.form.amount);
    },
    amountErrors() {
      if (!this.form.amount) return [];
      if (!this.parsedAmount || this.parsedAmount <= 0) return ["Informe um valor válido"];
      if (this.parsedAmount > this.availableAmount) return ["Valor maior que o saldo disponível"];
      return [];
    },
    requestDisabled() {
      return (
        this.loading ||
        !this.summary.account_ready ||
        !this.summary.payout_ready ||
        !this.availableAmount ||
        !this.parsedAmount ||
        this.amountErrors.length > 0
      );
    },
    setupMessage() {
      if (this.summary.account_configured && !this.summary.has_gateway_api_key) {
        return "A conta de recebimento está registrada. Para consultar saldo e solicitar repasses, conecte a credencial da conta.";
      }
      if (!this.summary.account_configured) return "Conecte a conta de recebimento do local para consultar saldo e solicitar repasses.";
      if (this.summary.gateway_error) return this.summary.gateway_error;
      if (!this.summary.payout_ready) return "Configure a chave Pix ou conta bancária de destino para liberar o saque.";
      return "";
    },
    availableCaption() {
      if (!this.summary.account_configured) return "Aguardando conexão da conta";
      if (!this.summary.account_ready) return "Conta registrada, aguardando credencial para consultar saldo";
      return "Saldo liberado para transferência";
    },
    destinationLabel() {
      if (!this.summary.payout_ready) return "Conta de repasse ainda não configurada";
      if (this.summary.payout_type === "pix") return "Destino configurado via Pix";
      return "Destino configurado por conta bancária";
    },
  },
  mounted() {
    this.load();
  },
  methods: {
    async load() {
      this.loading = true;
      try {
        const [summaryResponse, transfersResponse, accountResponse] = await Promise.all([
          axios.get("/payout-transfers/summary"),
          axios.get("/payout-transfers", { params: { limit: 20 } }),
          axios.get("/payment-accounts/current"),
        ]);

        this.paymentAccount = accountResponse.data.content || {};
        this.summary = this.summaryWithPaymentAccount(summaryResponse.data.content || {}, this.paymentAccount);
        const content = transfersResponse.data.content || [];
        this.transfers = content.data || content;

        if (!this.form.amount && this.summary.available_amount) {
          this.form.amount = this.formatInputMoney(this.summary.available_amount);
        }
      } catch (error) {
        this.notifyError(this.$codes ? this.$codes(error) : "Não foi possível carregar os repasses");
      } finally {
        this.loading = false;
      }
    },
    summaryWithPaymentAccount(summary = {}, account = {}) {
      const normalized = Object.assign(emptySummary(), summary);
      const hasWallet = Boolean(account.gateway_wallet_id || account.gateway_account_id);
      const hasApiKey = Boolean(account.has_gateway_api_key || account.gateway_api_key_encrypted || account.gateway_api_key);
      const accountExists = Boolean(account.id);

      if (accountExists || hasWallet) {
        normalized.account_id = normalized.account_id || account.id;
        normalized.account_configured = normalized.account_configured || hasWallet || accountExists;
        normalized.has_gateway_wallet = normalized.has_gateway_wallet || hasWallet;
        normalized.has_gateway_api_key = normalized.has_gateway_api_key || hasApiKey;
        normalized.account_ready = normalized.account_ready || hasApiKey;
        normalized.account_status = normalized.account_status || account.status || "pending";
        normalized.payout_type = normalized.payout_type || account.payout_type || null;
        normalized.payout_ready = normalized.payout_ready || this.paymentAccountPayoutReady(account);
      }

      if (normalized.account_configured && this.isLocalNotConnectedError(normalized.gateway_error)) {
        normalized.gateway_error = null;
      }

      return normalized;
    },
    isLocalNotConnectedError(message) {
      const normalized = String(message || "")
        .normalize("NFD")
        .replace(/[\u0300-\u036f]/g, "")
        .toLowerCase();

      return normalized.includes("conta de pagamento") && normalized.includes("nao conectada");
    },
    paymentAccountPayoutReady(account = {}) {
      if (account.payout_type === "pix") {
        return Boolean(account.payout_pix_key_type && account.payout_pix_key);
      }

      if (account.payout_type === "bank_account") {
        return Boolean(
          account.payout_bank_code &&
          account.payout_agency &&
          account.payout_account &&
          account.payout_account_digit &&
          account.payout_holder_name &&
          account.payout_holder_document
        );
      }

      return false;
    },
    async requestPayout() {
      if (this.requestDisabled) return;

      this.saving = true;
      try {
        await axios.post("/payout-transfers", { amount: this.parsedAmount });
        this.notifySuccess("Repasse solicitado");
        this.form.amount = "";
        await this.load();
      } catch (error) {
        this.notifyError(this.$codes ? this.$codes(error) : "Não foi possível solicitar o repasse");
      } finally {
        this.saving = false;
      }
    },
    parseMoney(value) {
      if (value === null || value === undefined || value === "") return 0;
      const normalized = String(value)
        .replace(/[^\d,.-]/g, "")
        .replace(/\./g, "")
        .replace(",", ".");
      const number = Number(normalized);
      return Number.isFinite(number) ? Number(number.toFixed(2)) : 0;
    },
    formatInputMoney(value) {
      return Number(value || 0).toLocaleString("pt-BR", {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      });
    },
    formatCurrency(value) {
      return Number(value || 0).toLocaleString("pt-BR", {
        style: "currency",
        currency: "BRL",
      });
    },
    formatDate(value) {
      if (!value) return "-";
      const date = new Date(value);
      if (Number.isNaN(date.getTime())) return "-";
      return date.toLocaleDateString("pt-BR", {
        day: "2-digit",
        month: "2-digit",
        year: "numeric",
        hour: "2-digit",
        minute: "2-digit",
      });
    },
    statusLabel(status) {
      const labels = {
        pending: "Pendente",
        processing: "Processando",
        done: "Concluído",
        error: "Erro",
      };
      return labels[status] || status || "-";
    },
    statusColor(status) {
      if (status === "done") return "secondary";
      if (status === "error") return "lightImportant";
      return "offWhite";
    },
    statusTextColor(status) {
      return status === "error" ? "red" : "primary";
    },
    notifySuccess(message) {
      this.$dialog && this.$dialog.notify && this.$dialog.notify.success(message, { position: "top-right", timeout: 3000 });
    },
    notifyError(message) {
      this.$dialog && this.$dialog.notify && this.$dialog.notify.error(message, { position: "top-right", timeout: 4000 });
    },
  },
};
</script>

<style scoped lang="scss">
.payout-page {
  display: flex;
  flex-direction: column;
  gap: 16px;
}

.payout-toolbar {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px;

  h2 {
    margin: 0;
    color: #111827;
    font-size: 22px;
    font-weight: 900;
  }

  p {
    margin: 4px 0 0;
    color: #66736b;
    font-size: 13px;
  }
}

.setup-alert {
  margin: 0;
}

.setup-alert-content {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
}

.summary-grid,
.payout-grid {
  display: grid;
  gap: 14px;
}

.summary-grid {
  grid-template-columns: repeat(3, minmax(0, 1fr));
}

.payout-grid {
  grid-template-columns: minmax(280px, 380px) minmax(0, 1fr);
  align-items: start;
}

.summary-panel,
.request-panel,
.history-panel {
  background: #ffffff;
  border: 1px solid #e1e7e0;
  border-radius: 8px;
}

.summary-panel {
  min-height: 132px;
  padding: 18px;
  display: flex;
  flex-direction: column;
  justify-content: space-between;

  span {
    color: #66736b;
    font-size: 13px;
    font-weight: 800;
  }

  strong {
    margin-top: 12px;
    color: #111827;
    font-size: 30px;
    line-height: 1;
  }

  small {
    margin-top: 12px;
    color: #66736b;
    font-size: 12px;
    line-height: 1.4;
  }

  &.available {
    border-color: rgba(0, 119, 67, 0.28);
    background: #f7fffb;

    strong {
      color: #007743;
    }
  }
}

.request-panel,
.history-panel {
  padding: 18px;
}

.panel-heading {
  margin-bottom: 16px;
  display: flex;
  justify-content: space-between;
  gap: 12px;

  h3 {
    margin: 0;
    color: #111827;
    font-size: 17px;
    font-weight: 900;
  }

  p {
    margin: 4px 0 0;
    color: #66736b;
    font-size: 13px;
    line-height: 1.4;
  }
}

.destination-line {
  min-height: 42px;
  margin: 12px 0 16px;
  padding: 10px 12px;
  display: flex;
  align-items: center;
  gap: 8px;
  color: #344054;
  background: #f7faf8;
  border: 1px solid #e1e7e0;
  border-radius: 8px;
  font-size: 13px;
}

.request-button {
  width: 100%;
}

.payout-table::v-deep {
  th {
    color: #66736b !important;
    font-size: 12px !important;
    text-transform: uppercase;
  }

  td {
    color: #111827;
  }
}

.empty-state {
  min-height: 240px;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 6px;

  strong {
    color: #111827;
  }

  span {
    color: #66736b;
    font-size: 13px;
  }
}

@media (max-width: 1020px) {
  .summary-grid,
  .payout-grid {
    grid-template-columns: 1fr;
  }
}

@media (max-width: 680px) {
  .setup-alert-content,
  .payout-toolbar {
    align-items: stretch;
    flex-direction: column;
  }

  .summary-panel strong {
    font-size: 26px;
  }
}
</style>
